import React, { useState, useEffect } from 'react';
import { Mail, Edit, Trash2, Plus, Check } from 'lucide-react';
import { useToast } from '../../components/ui/ToastProvider';
import { 
  EmailConfig, 
  getEmailConfigs, 
  deleteEmailConfig, 
  setDefaultEmailConfig 
} from '../../services/emailConfigService';
import EmailConfigModal from './EmailConfigModal';

interface EmailConfigListProps {
  tenantId?: string;
  onSelectEmail?: (email: string) => void;
  insideForm?: boolean; // Set to true when used inside another form
  onChanged?: (configs: EmailConfig[]) => void; // Notify parent when list changes
}

const EmailConfigList: React.FC<EmailConfigListProps> = ({ tenantId, onSelectEmail, insideForm = false, onChanged }) => {
  const [emailConfigs, setEmailConfigs] = useState<EmailConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<EmailConfig | undefined>(undefined);
  const toast = useToast();

  // Fetch email configurations
  const fetchEmailConfigs = async (): Promise<EmailConfig[]> => {
    setLoading(true);
    try {
      const response = await getEmailConfigs(tenantId);
      setEmailConfigs(response.email_configs);
      if (onChanged) {
        onChanged(response.email_configs);
      }
      return response.email_configs;
    } catch (error) {
      toast.error('Failed to load email configurations');
      console.error(error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmailConfigs();
  }, [tenantId]);

  // Handle adding new configuration
  const handleAddConfig = () => {
    setSelectedConfig(undefined);
    setIsModalOpen(true);
  };

  // Handle editing configuration
  const handleEditConfig = (config: EmailConfig) => {
    setSelectedConfig(config);
    setIsModalOpen(true);
  };

  // Handle deleting configuration
  const handleDeleteConfig = async (uuid: string) => {
    if (window.confirm('Are you sure you want to delete this email configuration?')) {
      try {
        await deleteEmailConfig(uuid);
        toast.success('Email configuration deleted');
        fetchEmailConfigs();
      } catch (error) {
        toast.error('Failed to delete email configuration');
      }
    }
  };

  // Handle setting default configuration
  const handleSetDefault = async (uuid: string) => {
    try {
      await setDefaultEmailConfig(uuid);
      toast.success('Default email configuration updated');
      fetchEmailConfigs();
    } catch (error) {
      toast.error('Failed to update default email configuration');
    }
  };

  // Handle selecting an email for the parent component
  const handleSelectEmail = (email: string) => {
    if (onSelectEmail) {
      onSelectEmail(email);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-white">Email Configurations</h3>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleAddConfig();
          }}
          className="flex items-center px-3 py-1.5 text-sm bg-primary hover:bg-primary-dark text-white rounded-md"
        >
          <Plus size={16} className="mr-1" />
          Add Email
        </button>
      </div>

      {loading ? (
        <div className="text-center py-4">
          <p className="text-gray-400">Loading...</p>
        </div>
      ) : emailConfigs.length === 0 ? (
        <div className="text-center py-8 border border-dashed border-gray-600 rounded-md">
          <Mail size={24} className="mx-auto text-gray-500 mb-2" />
          <p className="text-gray-400">No email configurations found</p>
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleAddConfig(); }}
            className="mt-2 text-primary hover:text-primary-light text-sm"
          >
            Add your first email configuration
          </button>
        </div>
      ) : (
        <div className="border border-primary/30 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-slate-800/70">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  SMTP Host
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Default
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {emailConfigs.map((config) => (
                <tr key={config.uuid} className="hover:bg-slate-800/50">
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleSelectEmail(config.email); }}
                      className="text-primary hover:text-primary-light hover:underline flex items-center"
                    >
                      <Mail size={14} className="mr-1.5" />
                      {config.email}
                    </button>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                    {config.smtp_host}:{config.smtp_port}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-center">
                    {config.is_default ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-900 text-green-300">
                        <Check size={12} className="mr-1" />
                        Default
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSetDefault(config.uuid!);
                        }}
                        className="text-xs text-gray-400 hover:text-primary"
                      >
                        Set as default
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleEditConfig(config);
                      }}
                      className="text-blue-500 hover:text-blue-400 mr-3"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDeleteConfig(config.uuid!);
                      }}
                      className="text-red-500 hover:text-red-400"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Email Configuration Modal */}
      <EmailConfigModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialData={selectedConfig}
        onSuccess={async () => {
          await fetchEmailConfigs();
        }}
        tenantId={tenantId}
        insideForm={insideForm}
      />
    </div>
  );
};

export default EmailConfigList;
